import { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { SaveCalculationDialog } from './SaveCalculationDialog';
import { SidebarHeader } from './components/SidebarHeader';
import { CalculationItem } from './components/CalculationItem';
import { useSidebar } from '@/context/SidebarContext';
import { useCalculations } from '@/hooks/useCalculations';
import { useToast } from '@/hooks/use-toast';
import { useDebounceValue } from '@/hooks/debounce/useDebounceValue';
import { SidebarProps, ToolType } from './types';
import { SavedCalculation } from '@/types/cvr';
import { Loader2, AlertCircle, FileText } from 'lucide-react';

// Map UI tool types to database table names
const toolTypeToTable: Record<ToolType, "cvr_calculations" | "evm_calculations" | "early_warning_register" | "target_cost_calculations" | "fatigue_calculations"> = {
  'cvr': 'cvr_calculations',
  'evm': 'evm_calculations',
  'target-cost': 'target_cost_calculations',
  'early-warning': 'early_warning_register',
  'fatigue': 'fatigue_calculations',
};

// Enhanced SidebarContent component with improved collapsed view
const EnhancedSidebarContent = ({
  calculations,
  isLoading,
  error,
  onLoad,
  onDelete,
  onRename,
  activeCalculationId,
  isMinimized,
}: {
  calculations: SavedCalculation[];
  isLoading: boolean;
  error: Error | null;
  onLoad: (calculation: SavedCalculation) => void;
  onDelete: (id: string) => Promise<void>;
  onRename: (id: string, newName: string) => Promise<void>;
  activeCalculationId: string | null;
  isMinimized: boolean;
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
        <p className="text-sm text-gray-600">{error.message}</p>
      </div>
    );
  }

  if (!calculations || calculations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <FileText className="h-8 w-8 text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">No saved calculations</p>
      </div>
    );
  }

  // Group calculations by month for better organization
  const groupedCalculations = calculations.reduce((acc, calc) => {
    const month = new Date(calc.date_saved).toLocaleString('default', { month: 'short', year: 'numeric' });
    if (!acc[month]) acc[month] = [];
    acc[month].push(calc);
    return acc;
  }, {} as Record<string, SavedCalculation[]>);

  return (
    <div className="flex-1 overflow-y-auto">
      {Object.entries(groupedCalculations).map(([month, monthCalculations]) => (
        <div key={month} className="mb-2">
          {!isMinimized && (
            <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase motion-safe:animate-fade-slide-in">
              {month}
            </div>
          )}
          <div className={isMinimized ? "px-2" : ""}>
            {monthCalculations.map((calculation) => (
              <CalculationItem
                key={calculation.id}
                calculation={calculation}
                onLoad={onLoad}
                onDelete={onDelete}
                onRename={onRename}
                isActive={calculation.id === activeCalculationId}
                isMinimized={isMinimized}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export function Sidebar({
  toolType,
  data,
  onSave,
  onLoad,
  className
}: SidebarProps) {
  const { isCollapsed, toggleCollapsed } = useSidebar();
  const tableType = toolTypeToTable[toolType];
  const {
    calculations,
    isLoading,
    error,
    loadCalculations,
    addCalculation,
    deleteCalculation,
    renameCalculation,
  } = useCalculations(tableType);

  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounceValue(searchQuery, 300);
  const [isSaveDialogOpen, setSaveDialogOpen] = useState(false);
  const [activeCalculationId, setActiveCalculationId] = useState<string | null>(null);
  const [temporarilyExpanded, setTemporarilyExpanded] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  // Force refresh flag
  const [refreshKey, setRefreshKey] = useState(0);

  // Effect to refresh calculations when refreshKey changes
  useEffect(() => {
    if (loadCalculations) {
      loadCalculations();
    }
  }, [loadCalculations, refreshKey]);

  // Handle hover expansion
  const handleMouseEnter = useCallback(() => {
    if (isCollapsed) {
      hoverTimeout.current = setTimeout(() => {
        setTemporarilyExpanded(true);
      }, 800); // 800ms delay before auto-expanding
    }
  }, [isCollapsed]);

  const handleMouseLeave = useCallback(() => {
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
    setTemporarilyExpanded(false);
  }, []);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout.current) {
        clearTimeout(hoverTimeout.current);
      }
    };
  }, []);

  const filteredCalculations = useCallback(() => {
    if (!calculations || !Array.isArray(calculations)) {
      return [];
    }

    if (!debouncedSearchQuery.trim()) return calculations;

    const query = debouncedSearchQuery.toLowerCase();
    return calculations.filter((calc) =>
      calc && calc.project_name?.toLowerCase().includes(query)
    );
  }, [calculations, debouncedSearchQuery]);

  const handleLoadCalculation = useCallback((calculation: SavedCalculation) => {
    onLoad(calculation);
    setActiveCalculationId(calculation.id);

    // Close sidebar on mobile after selection
    if (window.innerWidth < 768) {
      setTemporarilyExpanded(false);
    }
  }, [onLoad]);

  const handleSave = async (name: string, payload?: unknown) => {
    try {
      const dataToSave = payload ?? data;
      // Ensure data is not null before saving
      if (!dataToSave || typeof dataToSave !== 'object') {
        throw new Error('No data to save');
      }
      if (toolType !== 'fatigue') {
        // Validate required data structure based on tool type
        if (!(dataToSave as { details?: unknown }).details) {
          throw new Error('Invalid data structure: missing details');
        }
        // CVR and EVM have 'value', target-cost has 'ranges'
        const hasValidStructure = toolType === 'target-cost'
          ? 'ranges' in (dataToSave as Record<string, unknown>)
          : 'value' in (dataToSave as Record<string, unknown>);
        if (!hasValidStructure) {
          throw new Error('Invalid data structure');
        }
      }

      const savedCalculation = await onSave(name, dataToSave as typeof data);
      
      toast({
        title: 'Success',
        description: 'Calculation saved successfully.',
      });
      
      setSaveDialogOpen(false);
      
      // Add the saved calculation to the list if addCalculation is available
      if (savedCalculation && addCalculation) {
        addCalculation(savedCalculation);
      }
      
      // Force a refresh of the calculations list
      setRefreshKey(prev => prev + 1);
      
      // If no direct addCalculation method is available, force refresh after a short delay
      setTimeout(() => {
        if (loadCalculations) {
          loadCalculations();
        }
      }, 300);
      
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save calculation.',
        variant: 'destructive',
      });
    }
  };

  // Apply temporary expansion
  const effectivelyCollapsed = isCollapsed && !temporarilyExpanded;

  return (
    <div
      className={cn(
        "flex flex-col bg-gradient-to-b from-background to-muted/10",
        "border-r border-border/60 shadow-[1px_0_6px_-2px_rgba(0,0,0,0.06)]",
        "overflow-hidden", // Prevent content bleed during animation
        "motion-safe:transition-[width] motion-safe:duration-300 motion-safe:ease-sidebar",
        effectivelyCollapsed ? "w-20" : "w-80",
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <SidebarHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSaveClick={() => setSaveDialogOpen(true)}
        onToggleCollapse={toggleCollapsed}
        isMinimized={effectivelyCollapsed}
      />
      <EnhancedSidebarContent
        calculations={filteredCalculations()}
        isLoading={isLoading}
        error={error}
        onLoad={handleLoadCalculation}
        onDelete={deleteCalculation}
        onRename={renameCalculation}
        activeCalculationId={activeCalculationId}
        isMinimized={effectivelyCollapsed}
      />
      <SaveCalculationDialog
        open={isSaveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        data={data}
        onSave={handleSave}
        toolType={toolType}
      />
    </div>
  );
}
